import { Eye, EyeOff, Lock, Mail, User as UserIcon } from "lucide-react";
import * as React from "react";

import { authApi } from "@/api/auth";
import { ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { BrandLogo, GradientText } from "@/components/ui/gradient";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth";

type Tab = "login" | "register";

export function LoginPage(): JSX.Element {
  const setAuth = useAuthStore((s) => s.setAuth);
  const [tab, setTab] = React.useState<Tab>("login");

  // Login form state
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);

  // Register-only fields
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [displayName, setDisplayName] = React.useState("");

  const [error, setError] = React.useState<string | null>(null);
  const [info, setInfo] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  const switchTab = (next: Tab): void => {
    setTab(next);
    setError(null);
    setInfo(null);
    if (next === "register") {
      setInfo(
        "Tài khoản đầu tiên đăng ký sẽ được cấp role admin tự động (bootstrap hệ thống).",
      );
    }
  };

  const submit = async (e?: React.FormEvent): Promise<void> => {
    e?.preventDefault();
    setLoading(true);
    setError(null);
    setInfo(null);
    try {
      let tokens;
      if (tab === "register") {
        if (password.length < 6) {
          throw new Error("Mật khẩu phải ≥ 6 ký tự");
        }
        if (password !== confirmPassword) {
          throw new Error("Mật khẩu xác nhận không khớp");
        }
        tokens = await authApi.register(
          email.trim().toLowerCase(),
          password,
          displayName.trim() || undefined,
        );
      } else {
        tokens = await authApi.login(email.trim().toLowerCase(), password);
      }
      useAuthStore.setState({ accessToken: tokens.access_token });
      const user = await authApi.me();
      setAuth(user, tokens.access_token);
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? e.status === 401
            ? "Email hoặc mật khẩu không đúng"
            : e.status === 409
              ? "Email đã được đăng ký — chuyển sang Đăng nhập"
              : e.message
          : e instanceof Error
            ? e.message
            : String(e);
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const isRegister = tab === "register";
  const submitDisabled =
    loading ||
    email.length < 3 ||
    password.length < 1 ||
    (isRegister && (confirmPassword.length < 1 || password !== confirmPassword));

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-mesh p-4 relative overflow-hidden">
      {/* Decorative gradient orbs */}
      <div
        aria-hidden
        className="absolute -top-40 -left-40 size-96 rounded-full bg-gradient-brand opacity-20 blur-3xl"
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -right-40 size-96 rounded-full bg-gradient-brand opacity-15 blur-3xl"
      />
      <Card className="w-full max-w-md relative z-10 shadow-card-lg backdrop-blur-xl bg-background/95">
        <CardHeader className="text-center pb-4">
          <div className="flex justify-center mb-3">
            <BrandLogo size={80} />
          </div>
          <CardTitle className="text-2xl">
            <GradientText as="span" className="font-bold">
              Trợ lý AI
            </GradientText>
          </CardTitle>
          <CardDescription>
            Conversational ERP Agent · cuonglv_agent_ai
          </CardDescription>
        </CardHeader>

        {/* Tabs */}
        <div className="px-6">
          <div className="flex border rounded-md p-0.5 bg-muted/40">
            <button
              type="button"
              onClick={() => switchTab("login")}
              className={cn(
                "flex-1 text-sm py-1.5 rounded font-medium transition-colors",
                tab === "login"
                  ? "bg-background shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              Đăng nhập
            </button>
            <button
              type="button"
              onClick={() => switchTab("register")}
              className={cn(
                "flex-1 text-sm py-1.5 rounded font-medium transition-colors",
                tab === "register"
                  ? "bg-background shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              Đăng ký
            </button>
          </div>
        </div>

        <form onSubmit={(e) => void submit(e)}>
          <CardContent className="space-y-3 pt-4">
            <div className="space-y-1.5">
              <label htmlFor="email" className="text-sm font-medium">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-9"
                  autoComplete="email"
                  required
                  autoFocus
                />
              </div>
            </div>

            {isRegister ? (
              <div className="space-y-1.5">
                <label htmlFor="displayName" className="text-sm font-medium">
                  Tên hiển thị (tùy chọn)
                </label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                  <Input
                    id="displayName"
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="pl-9"
                    autoComplete="name"
                    placeholder="VD: Nguyễn Văn A"
                  />
                </div>
              </div>
            ) : null}

            <div className="space-y-1.5">
              <label htmlFor="password" className="text-sm font-medium">
                Mật khẩu {isRegister ? "(≥ 6 ký tự)" : ""}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-9 pr-9"
                  autoComplete={isRegister ? "new-password" : "current-password"}
                  required
                  minLength={isRegister ? 6 : 1}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
                >
                  {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>
            </div>

            {isRegister ? (
              <div className="space-y-1.5">
                <label htmlFor="confirmPassword" className="text-sm font-medium">
                  Xác nhận mật khẩu
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-9"
                    autoComplete="new-password"
                    required
                    minLength={6}
                  />
                </div>
                {confirmPassword.length > 0 && password !== confirmPassword ? (
                  <p className="text-xs text-destructive">
                    Mật khẩu xác nhận không khớp
                  </p>
                ) : null}
              </div>
            ) : null}

            {info ? (
              <div className="rounded-md bg-primary/10 border border-primary/30 p-2.5 text-xs text-foreground">
                {info}
              </div>
            ) : null}

            {error ? (
              <div className="rounded-md bg-destructive/10 border border-destructive/30 p-2.5 text-sm text-destructive">
                {error}
              </div>
            ) : null}
          </CardContent>

          <CardFooter className="flex-col gap-2">
            <Button
              type="submit"
              variant="brand"
              className="w-full h-10"
              disabled={submitDisabled}
            >
              {loading
                ? isRegister
                  ? "Đang đăng ký..."
                  : "Đang đăng nhập..."
                : isRegister
                  ? "Đăng ký"
                  : "Đăng nhập"}
            </Button>
            {!isRegister ? (
              <p className="text-[10px] text-center text-muted-foreground">
                <button
                  type="button"
                  onClick={() => {
                    window.location.hash = "/forgot-password";
                  }}
                  className="text-primary underline hover:opacity-80"
                >
                  Quên mật khẩu?
                </button>
              </p>
            ) : null}
            <p className="text-[10px] text-center text-muted-foreground">
              {isRegister
                ? "Đã có tài khoản? "
                : "Chưa có tài khoản? "}
              <button
                type="button"
                onClick={() => switchTab(isRegister ? "login" : "register")}
                className="text-primary underline hover:opacity-80"
              >
                {isRegister ? "Đăng nhập" : "Đăng ký ngay"}
              </button>
            </p>
            <p className="text-[10px] text-center text-muted-foreground">
              Doc 47 · Native auth · Odoo identity tùy chọn (admin map sau)
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
